'use client';

import { Plus } from 'lucide-react';
import { Button } from '@/shared/ui/shadcn/button';
import { cn } from '@/shared/lib/cn';

interface CircularAddButtonProps {
  onClick?: () => void;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'outline' | 'accent';
  className?: string;
  ariaLabel?: string;
  children?: React.ReactNode;
}

export function CircularAddButton({
  onClick,
  disabled = false,
  size = 'md',
  variant = 'outline',
  className,
  ariaLabel = 'Add',
  children,
}: CircularAddButtonProps) {
  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12',
  };

  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };

  const variantClasses = {
    outline: cn(
      'rounded-full border-2 transition-all',
      'bg-background hover:bg-primary hover:text-primary-foreground hover:border-primary'
    ),
    accent: cn(
      'rounded-full transition-all',
      'bg-primary text-primary-foreground hover:bg-primary/90'
    ),
  };

  // If children are provided, wrap them with the button
  if (children) {
    return (
      <Button
        onClick={onClick}
        disabled={disabled}
        variant={variant === 'accent' ? 'default' : 'outline'}
        size="icon"
        className={cn(variantClasses[variant], sizeClasses[size], className)}
        aria-label={ariaLabel}
      >
        {children}
      </Button>
    );
  }

  return (
    <Button
      onClick={onClick}
      disabled={disabled}
      variant={variant === 'accent' ? 'default' : 'outline'}
      size="icon"
      className={cn(variantClasses[variant], sizeClasses[size], className)}
      aria-label={ariaLabel}
    >
      <Plus className={iconSizes[size]} />
    </Button>
  );
}
